import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gem, CheckCircle2, Frame } from 'lucide-react';
import Reveal from '../components/Reveal';
import pictureFraming from '../assets/products/picture-framing.svg';
import namePlates from '../assets/products/name-plates.svg';
import glassPanel from '../assets/products/glass-panel.svg';
import aluminiumSliding from '../assets/products/aluminium-sliding.svg';
import stainedGlass from '../assets/products/stained-glass.svg';
import mirrorsEtching from '../assets/products/mirrors-etching.svg';
import ledMirrors from '../assets/products/led-mirrors.svg';
import wallDecor from '../assets/products/wall-decor.svg';

const framingProducts = [
  {
    name: 'Picture Framing',
    img: pictureFraming,
    tagline: 'The best framing under one roof',
    description:
      "Framing is our speciality and the reason India Glass Works was founded. We frame photographs, wedding albums, certificates, paintings and canvas art, choosing the moulding, mount and glass that suit the piece.",
    features: [
      'Custom sizes and shapes, made to order',
      'Wood and metal frame mouldings, in a range of finishes',
      'Plain, non-reflective or UV-protective glass fronting',
      'Mounting, matting and mounting boards',
      'Frame repair and restoration for older pieces'
    ],
    applications: 'Family photos, wedding albums, certificates, paintings, canvas and fabric art'
  },
  {
    name: 'Name Plates',
    img: namePlates,
    tagline: 'A refined first impression',
    description:
      'Custom name plates for homes, offices and shops, engraved or printed on glass, acrylic or metal, and finished to match your door or entrance.',
    features: [
      'Glass, acrylic and metal plate options',
      'Engraved or printed lettering and logos',
      'Backlit and LED-lit designs available',
      'Door, gate, building and cabin name plates'
    ],
    applications: 'Homes, apartments, offices, shops and cabins'
  }
];

const glassProducts = [
  {
    name: 'Glass Panels & Shelves',
    img: glassPanel,
    tagline: 'Custom-cut for every space',
    description:
      'We supply and fit glass panels, table tops, cabinet glass and display shelves, cut to size in clear or toughened glass for safety and durability.',
    features: [
      'Clear, tinted and toughened glass',
      'Table tops, cabinet and display shelving',
      'Edge polishing and safety-edge finishing',
      'Fitted on-site by our team'
    ],
    applications: 'Cabinets, showcases, table tops, partitions and shelving'
  },
  {
    name: 'Aluminium Slidings',
    img: aluminiumSliding,
    tagline: 'Smooth, durable, weatherproof',
    description:
      'Aluminium sliding windows and doors that glide smoothly and stand up to Pune weather, fitted with quality tracks and hardware.',
    features: [
      'Sliding windows, doors and partitions',
      'Choice of aluminium profile finishes',
      'Mesh options for ventilation with protection',
      'Measured and fitted to your openings'
    ],
    applications: 'Windows, balcony doors, partitions and grills'
  },
  {
    name: 'Stained Glass',
    img: stainedGlass,
    tagline: 'Colour that lasts a lifetime',
    description:
      'Traditional leaded and stained glass work for windows, door inserts and decorative panels, in custom colours and patterns.',
    features: [
      'Traditional leaded-glass technique',
      'Custom colour palettes and patterns',
      'Decorative and religious motifs on request',
      'Panels for windows, doors and partitions'
    ],
    applications: 'Windows, doors, room dividers and pooja units'
  },
  {
    name: 'Mirrors & Etching',
    img: mirrorsEtching,
    tagline: 'A frosted, modern finish',
    description:
      'Mirror supply and fitting alongside frosted glass etching for a refined, private finish on doors, partitions and washrooms.',
    features: [
      'Plain and frosted mirror supply',
      'Custom frosted/etched patterns',
      'Privacy film and partition glass',
      'Bathroom and vanity mirror fitting'
    ],
    applications: 'Bathrooms, partitions, wardrobe doors and washrooms'
  }
];

const decorProducts = [
  {
    name: 'LED Mirrors',
    img: ledMirrors,
    tagline: 'Warm light, modern look',
    description:
      'LED-lit mirrors for bathrooms, dressing areas and entryways, bringing soft, even light and a contemporary finish to any room.',
    features: [
      'Round, oval and custom shapes',
      'Warm or cool LED lighting options',
      'Wall-mounted fitting included',
      'Suited to bathrooms and dressing areas'
    ],
    applications: 'Bathrooms, dressers, entryways and salons'
  },
  {
    name: 'Decorative Wall Art',
    img: wallDecor,
    tagline: 'Finishing touches for your walls',
    description:
      'Sunburst mirrors and decorative glass wall pieces that add a finishing touch to living rooms, entryways and hallways.',
    features: [
      'Sunburst and geometric mirror designs',
      'Decorative glass wall panels',
      'Custom sizing to suit your wall',
      'Ready to hang, fitted on request'
    ],
    applications: 'Living rooms, entryways, hallways and offices'
  }
];

const ProductSection = ({ title, eyebrow, intro, products, highlight }) => (
  <div className={highlight ? 'bg-cream-100 py-20' : 'bg-white py-20'}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center mb-14">
        <span className="text-crimson-600 font-semibold tracking-widest uppercase text-xs">{eyebrow}</span>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-ink-700 mt-3">{title}</h2>
        {intro && <p className="text-ink-500 max-w-2xl mx-auto mt-4">{intro}</p>}
        <div className="ornament-divider mt-6"><Gem size={16} className="text-gold-500" /></div>
      </Reveal>

      <div className="space-y-14">
        {products.map((product, index) => (
          <Reveal
            key={product.name}
            animation={index % 2 === 1 ? 'right' : 'left'}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${
              index % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
            }`}
          >
            <div className="frame-corners relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-gold-200/50 to-crimson-100/50 rounded-2xl blur-lg"></div>
              <img
                src={product.img}
                alt={product.name}
                className="relative w-full rounded-xl shadow-lg border border-gold-100"
              />
            </div>
            <div>
              {product.tagline && (
                <span className="inline-flex items-center gap-2 text-gold-700 bg-gold-100 border border-gold-300 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wide mb-4">
                  <Frame size={14} /> {product.tagline}
                </span>
              )}
              <h3 className="font-display text-2xl md:text-3xl font-bold text-ink-700 mb-4">{product.name}</h3>
              <p className="text-ink-500 leading-relaxed mb-5">{product.description}</p>
              <ul className="space-y-2 mb-5">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-ink-600 text-sm">
                    <CheckCircle2 size={18} className="text-crimson-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-ink-500">
                <span className="font-semibold text-ink-600">Popular for: </span>
                {product.applications}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

const Products = () => {
  return (
    <div className="pt-16 bg-cream-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-crimson-500 via-crimson-600 to-crimson-700 text-white py-20 relative overflow-hidden">
        <div className="absolute -top-16 -left-16 w-72 h-72 bg-gold-300/20 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <span className="inline-block mb-4 text-gold-200 tracking-[0.25em] uppercase text-xs font-semibold border border-white/30 rounded-full px-4 py-1.5">
            Framing First, Glass Always
          </span>
          <h1 className="font-display text-5xl md:text-6xl font-bold mb-6 animate-fadeInUp">
            Our Products
          </h1>
          <div className="w-24 h-1 bg-gold-300 mx-auto mb-6"></div>
          <p className="text-xl max-w-3xl mx-auto text-cream-100/90 animate-fadeInUp" style={{ animationDelay: '0.15s' }}>
            Picture framing and name plates are where we started — and we've grown into a full glass and
            mirror workshop alongside it. Explore what we make, below.
          </p>
        </div>
      </div>

      <ProductSection
        eyebrow="Our Speciality"
        title="Framing"
        intro="The best framing under one roof — this is what India Glass Works is known for in Pune."
        products={framingProducts}
      />

      <ProductSection
        eyebrow="Also Available"
        title="Glass Work"
        intro="A full range of glass services to complement our framing craft."
        products={glassProducts}
        highlight
      />

      <ProductSection
        eyebrow="Also Available"
        title="Mirrors & Décor"
        intro="Finishing touches that bring light and character to your space."
        products={decorProducts}
      />

      {/* CTA */}
      <div className="bg-gradient-to-br from-crimson-500 via-crimson-600 to-crimson-700 text-white py-16">
        <Reveal animation="scale" className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-6">
            Have Something to Frame?
          </h2>
          <p className="text-lg mb-8 text-cream-100/90">
            Bring in your photo, artwork or certificate, and we'll help you choose the perfect frame.
          </p>
          <Link
            to="/contact"
            className="glass-sheen inline-flex items-center bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-500 hover:to-gold-600 text-ink-700 px-10 py-4 rounded-lg text-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-gold-500/30"
          >
            Get in Touch
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </Reveal>
      </div>
    </div>
  );
};

export default Products;
